import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import {
  EditOutlined,
  CloseOutlined,
  PlusOutlined,
  AimOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import css from './CoveragePlanStep.module.scss';

/**
 * One search in the coverage plan ("agent"). `seed` marks the user's exact
 * target (always item 0) — read-only and non-removable; it's what we reviewed
 * and the anchor for "just my exact search".
 */
export interface PlanAgent {
  /** Stable identity so the list is never keyed by mutable array index. */
  id: string;
  role: string;
  location: string;
  rationale: string;
  seed: boolean;
}

interface CoveragePlanStepProps {
  items: PlanAgent[];
  loading: boolean;
  error: boolean;
  usedSlots: number;
  maxPerUser: number;
  maxPerBatch: number;
  remaining: number;
  selectedCount: number;
  canAdd: boolean;
  /** True while the pack is being created — freeze per-card editing/removal. */
  disabled: boolean;
  /**
   * Remote search: every card is a ROLE variant of one destination market, so
   * the location is not editable per card and an added card inherits it. The
   * permit answer the user gave names exactly this destination; a card pointed
   * elsewhere would ship with no answer at all.
   */
  fixedLocation?: string;
  onRemove: (id: string) => void;
  onEditCommit: (id: string, role: string, location: string) => void;
  onAdd: () => void;
}

const AgentCard: React.FC<{
  item: PlanAgent;
  index: number;
  disabled: boolean;
  fixedLocation?: string;
  onRemove: () => void;
  onEditCommit: (role: string, location: string) => void;
}> = ({ item, index, disabled, fixedLocation, onRemove, onEditCommit }) => {
  // Manually-added agents (empty role) open straight into edit mode.
  const [editing, setEditing] = useState(!item.seed && item.role === '');
  const [role, setRole] = useState(item.role);
  const [location, setLocation] = useState(item.location);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  // Return focus to the Edit button when leaving edit mode, so keyboard/screen-
  // reader users aren't dropped at the document root after committing.
  useEffect(() => {
    if (wasEditing.current && !editing) {
      editBtnRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  const commit = () => {
    onEditCommit(role.trim(), (fixedLocation ?? location).trim());
    setEditing(false);
  };

  return (
    <div
      className={`${css.agent} ${item.seed ? css.seed : ''}`}
      style={{ animationDelay: `${0.12 + index * 0.06}s` }}
    >
      <div className={css.top}>
        <span className={css.idx}>{index + 1}</span>

        <div className={css.main}>
          {editing ? (
            <div className={css.editing}>
              <Input
                size="small"
                autoFocus
                value={role}
                maxLength={100}
                placeholder="Job title"
                aria-label="Agent role"
                onChange={(e) => setRole(e.target.value)}
                onPressEnter={commit}
              />
              {fixedLocation === undefined ? (
                <Input
                  size="small"
                  value={location}
                  maxLength={100}
                  placeholder="Location"
                  aria-label="Agent location"
                  onChange={(e) => setLocation(e.target.value)}
                  onPressEnter={commit}
                />
              ) : (
                <div className={css.loc}>
                  <EnvironmentOutlined /> {fixedLocation}
                </div>
              )}
              <button type="button" className={css.done} onClick={commit} disabled={disabled}>
                Done
              </button>
            </div>
          ) : (
            <>
              {item.seed && (
                <span className={css.pillTarget}>
                  <AimOutlined /> Your target
                </span>
              )}
              <div className={css.role}>{item.role}</div>
              <div className={css.loc}>
                <EnvironmentOutlined /> {item.location}
              </div>
            </>
          )}
        </div>

        {!item.seed && !editing && (
          <div className={css.controls}>
            <button
              type="button"
              ref={editBtnRef}
              className={css.iconBtn}
              aria-label={`Edit ${item.role || 'agent'}`}
              disabled={disabled}
              onClick={() => setEditing(true)}
            >
              <EditOutlined />
            </button>
            <button
              type="button"
              className={css.iconBtn}
              aria-label={`Remove ${item.role || 'agent'}`}
              disabled={disabled}
              onClick={onRemove}
            >
              <CloseOutlined />
            </button>
          </div>
        )}
      </div>

      {!editing && item.rationale && <div className={css.why}>{item.rationale}</div>}
    </div>
  );
};

/**
 * The Coverage Plan step. While `/plan` is in flight it shows the "mapping"
 * beat; on failure a quiet error (the footer owns the retry); otherwise the
 * reviewable pack of agents — every card shown is included, removing one
 * prunes it. The smart default is the whole pack in one tap.
 */
const CoveragePlanStep: React.FC<CoveragePlanStepProps> = ({
  items,
  loading,
  error,
  usedSlots,
  maxPerUser,
  maxPerBatch,
  remaining,
  selectedCount,
  canAdd,
  disabled,
  fixedLocation,
  onRemove,
  onEditCommit,
  onAdd,
}) => {
  if (loading) {
    return (
      <div className={css.mapping} role="status" aria-live="polite">
        <div className={css.radar} aria-hidden="true">
          <span className={css.ring} />
          <span className={css.pulse} />
          <span className={css.pulse} />
          <span className={css.pulse} />
          <span className={css.sweep} />
          <span className={css.core}>
            <AimOutlined />
          </span>
        </div>
        <h3>The Beast is mapping your coverage…</h3>
        <p>Lining up the searches that widen your reach.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.errorState}>
        <h3>We couldn’t map your coverage</h3>
        <p>Something went wrong mapping your coverage — you can try again.</p>
      </div>
    );
  }

  // At the per-user ceiling the backend returns an empty plan — explain the
  // limit and the way out instead of an empty, confusing "0 searches" list.
  if (items.length === 0) {
    return (
      <div className={css.errorState}>
        <h3>You’ve reached your search limit</h3>
        <p>
          You’re at the maximum of {maxPerUser} searches — pause or delete one to
          add more, then try again.
        </p>
      </div>
    );
  }

  const total = usedSlots + selectedCount;

  return (
    <div className={css.plan}>
      <h3 className={css.heading}>Your search coverage</h3>
      <p className={css.sub}>
        The Beast lined up {items.length} parallel{' '}
        {items.length === 1 ? 'search' : 'searches'} to widen your reach. Start
        them all in one tap — or fine-tune below.
      </p>

      <div className={css.meter}>
        <div
          className={css.track}
          role="progressbar"
          aria-label="Search slots used"
          aria-valuemin={0}
          aria-valuemax={maxPerUser}
          aria-valuenow={total}
        >
          <div
            className={css.fill}
            style={{ width: `${Math.min(100, (total / maxPerUser) * 100)}%` }}
          />
        </div>
        <span className={css.meterLabel}>
          You’ll have <b>{total}</b> of {maxPerUser} searches
        </span>
      </div>

      <div className={css.agents}>
        {items.map((item, i) => (
          <AgentCard
            key={item.id}
            item={item}
            index={i}
            disabled={disabled}
            fixedLocation={fixedLocation}
            onRemove={() => onRemove(item.id)}
            onEditCommit={(role, location) => onEditCommit(item.id, role, location)}
          />
        ))}
      </div>

      {canAdd && !disabled ? (
        <button type="button" className={css.addRow} onClick={onAdd}>
          <PlusOutlined /> Add another agent
        </button>
      ) : (
        <>
          <button type="button" className={`${css.addRow} ${css.addDisabled}`} disabled>
            <PlusOutlined /> Add another agent
          </button>
          {!canAdd && (
            <p className={css.addHint}>
              You can start up to {maxPerBatch} at once · {remaining}{' '}
              {remaining === 1 ? 'slot' : 'slots'} left of {maxPerUser}.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CoveragePlanStep;
